import React, { Component } from 'react';
import {
    Dialog, DialogContent, DialogActions, Button, TextField,
    IconButton, Typography, Divider, CircularProgress
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EmailIcon from '@material-ui/icons/Email';
import LockIcon from '@material-ui/icons/Lock';
import { injectIntl } from 'react-intl';

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Modal de 2FA do login. Pede o código 6 dígitos enviado por email.
 *
 * Props:
 *   open (bool)
 *   email (string) — exibido só pra contexto visual
 *   onClose() — fecha (cancela login)
 *   onConfirm(code) — parent valida o código chamando /users/login com email+senha+code
 *   onResend() — parent chama /users/login/request-2fa
 *   submitting (bool) — durante validação
 *   resending (bool) — durante reenvio
 *   submitError (string) — erro genérico exibido inline
 */
class LoginTwoFactorDialog extends Component {
    state = { code: '', secondsLeft: 0 };
    _timer = null;

    componentDidUpdate(prevProps) {
        if (!prevProps.open && this.props.open) {
            this.setState({ code: '' });
            this.startCooldown();
        }
        if (prevProps.open && !this.props.open) {
            this.stopTimer();
        }
    }

    componentWillUnmount() { this.stopTimer(); }

    startCooldown = () => {
        this.stopTimer();
        this.setState({ secondsLeft: RESEND_COOLDOWN_SECONDS });
        this._timer = setInterval(() => {
            this.setState(s => {
                const next = s.secondsLeft - 1;
                if (next <= 0) this.stopTimer();
                return { secondsLeft: Math.max(0, next) };
            });
        }, 1000);
    }

    stopTimer = () => {
        if (this._timer) { clearInterval(this._timer); this._timer = null; }
    }

    handleResend = () => {
        if (this.props.onResend) this.props.onResend();
        this.startCooldown();
    }

    handleConfirm = () => {
        if (this.props.onConfirm) this.props.onConfirm(this.state.code);
    }

    canConfirm = () => !this.props.submitting && /^\d{4,10}$/.test(this.state.code || '');

    render() {
        const { open, email, onClose, submitting, resending, submitError, intl } = this.props;
        const { code, secondsLeft } = this.state;
        return (
            <Dialog
                open={open}
                onClose={(_, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    if (onClose) onClose();
                }}
                disableEscapeKeyDown
                maxWidth="xs"
                fullWidth
                PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', backgroundColor: '#f5f7fa' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <LockIcon style={{ color: '#8BC34A', marginRight: 12 }} />
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            {intl.formatMessage({ id: 'login.2fa.title' })}
                        </Typography>
                    </div>
                    <IconButton size="small" onClick={onClose} disabled={submitting}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
                <Divider />
                <DialogContent style={{ padding: 24 }}>
                    <Typography variant="body2" style={{ marginBottom: 12, color: '#555' }}>
                        {intl.formatMessage({ id: 'login.2fa.subtitle' })}
                    </Typography>
                    {email && (
                        <Typography variant="caption" style={{ color: '#888', display: 'block', marginBottom: 16 }}>
                            <EmailIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />
                            {email}
                        </Typography>
                    )}

                    <TextField
                        label={intl.formatMessage({ id: 'login.2fa.code' })}
                        value={code}
                        onChange={(e) => this.setState({ code: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                        variant="outlined"
                        fullWidth
                        disabled={submitting}
                        autoComplete="one-time-code"
                        inputProps={{
                            style: { fontFamily: 'monospace', fontSize: 24, letterSpacing: 8, textAlign: 'center' },
                            inputMode: 'numeric',
                        }}
                        placeholder="000000"
                    />

                    {submitError && (
                        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 6, backgroundColor: '#ffebee', borderLeft: '4px solid #b71c1c', color: '#b71c1c', fontSize: 13 }}>
                            ⚠ {submitError}
                        </div>
                    )}

                    <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" style={{ color: '#888' }}>
                            {intl.formatMessage({ id: 'login.2fa.expires' })}
                        </Typography>
                        <Button
                            size="small"
                            startIcon={resending ? <CircularProgress size={14} /> : <EmailIcon fontSize="small" />}
                            onClick={this.handleResend}
                            disabled={secondsLeft > 0 || resending || submitting}
                            style={{ textTransform: 'none' }}
                        >
                            {secondsLeft > 0
                                ? intl.formatMessage({ id: 'login.2fa.resend.in' }, { seconds: secondsLeft })
                                : intl.formatMessage({ id: 'login.2fa.resend' })}
                        </Button>
                    </div>
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px 16px', backgroundColor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                    <Button onClick={onClose} disabled={submitting} style={{ textTransform: 'none' }}>
                        {intl.formatMessage({ id: 'cancel' })}
                    </Button>
                    <Button
                        onClick={this.handleConfirm}
                        disabled={!this.canConfirm()}
                        variant="contained"
                        color="primary"
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {intl.formatMessage({ id: 'login.2fa.confirm' })}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default injectIntl(LoginTwoFactorDialog);
